/* =============================================================================
   splux-blog-gallery.js — Build Gallery dropdowns + filter navigation
   =============================================================================

   Progressively enhances the markup from sections/splux-blog-gallery.liquid.
   Without this file the page still renders the newest posts as a normal listing
   and the dropdowns are inert, so nothing is lost.

   1. Dropdowns — mirrors the interaction of sections/product-filter-finder.liquid:
      one panel open at a time, a search box that narrows the option list, chevron
      rotation driven by an `is-open` class, click-outside and Escape to close.

   2. Filtering — a selection navigates. Nothing is filtered in the browser: the
      gallery renders one page of articles, so filtering those in place would only
      ever search 7 of the ~200 builds on the blog.

   Where a selection goes
   ----------------------
   A selection navigates to a real tag archive:

       /blogs/<blog>/tagged/<tag>

   Shopify filters the blog server-side and paginates the result, so the count,
   the pager and the URL are all correct, and the archive can be indexed.

   The cost is that a tag archive takes exactly ONE tag, so the three dropdowns
   act as a single filter at a time: choosing an option in one clears the others,
   and the bar always reflects the archive the page is actually showing.

   The tag comes from the option's `data-tagged`, filled by the section from the
   canonical tag in field 3 of the option list (falling back to the option's first
   back-end tag). Browse by model links use the same field, so the two can never
   disagree.

   The `tag:` article-search route in sections/page-search.liquid is no longer
   reached from here; it is left in place but dormant.
   ============================================================================= */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-splux-bg]';
  function navigate(url) {
    if (!url) return;
    window.location.assign(url);
  }

  /* ---------------------------------------------------------------------------
     Dropdown
     --------------------------------------------------------------------------- */

  function initDropdown(dropdown, context) {
    var trigger = dropdown.querySelector('[data-splux-bg-trigger]');
    var valueEl = dropdown.querySelector('[data-splux-bg-value]');
    var panel = dropdown.querySelector('[data-splux-bg-panel]');
    var search = dropdown.querySelector('[data-splux-bg-search]');
    var options = Array.prototype.slice.call(
      dropdown.querySelectorAll('[data-splux-bg-option]')
    );

    if (!trigger || !valueEl || !panel) return null;

    function close() {
      dropdown.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
    }

    function open() {
      context.closeAll(dropdown);
      dropdown.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      if (search) {
        search.value = '';
        options.forEach(function (option) {
          option.classList.remove('is-hidden');
        });
        search.focus();
      }
    }

    function findOption(slug) {
      var found = null;
      options.forEach(function (option) {
        if ((option.getAttribute('data-value') || '') === slug) found = option;
      });
      return found;
    }

    /* Keeps the trigger copy and the selected row in one place, so a preset
       restored from current_tags and a real click render the same UI. */
    function select(slug, skipNavigate) {
      var match = findOption(slug);

      options.forEach(function (option) {
        var isMatch = option === match;
        option.classList.toggle('is-selected', isMatch);
        option.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });

      dropdown.setAttribute('data-value', match ? slug : '');
      valueEl.textContent = match
        ? match.textContent.trim()
        : dropdown.getAttribute('data-splux-bg-all') || '';

      close();

      if (!skipNavigate) context.apply(api);
    }

    trigger.addEventListener('click', function (event) {
      event.stopPropagation();
      if (dropdown.classList.contains('is-open')) close();
      else open();
    });

    options.forEach(function (option) {
      option.addEventListener('click', function (event) {
        event.stopPropagation();
        select(option.getAttribute('data-value') || '');
      });
    });

    if (search) {
      search.addEventListener('click', function (event) {
        event.stopPropagation();
      });
      search.addEventListener('input', function () {
        var query = search.value.toLowerCase();
        options.forEach(function (option) {
          var hit = option.textContent.toLowerCase().indexOf(query) !== -1;
          option.classList.toggle('is-hidden', !hit);
        });
      });
    }

    dropdown.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && dropdown.classList.contains('is-open')) {
        close();
        trigger.focus();
      }
    });

    var api = {
      element: dropdown,
      value: function () {
        return dropdown.getAttribute('data-value') || '';
      },
      tagged: function () {
        var match = findOption(dropdown.getAttribute('data-value') || '');
        return match ? match.getAttribute('data-tagged') || '' : '';
      },
      select: select,
      close: close
    };

    return api;
  }

  /* ---------------------------------------------------------------------------
     Sticky filter offset
     ---------------------------------------------------------------------------

     The bar sticks under the theme's header. That header is fixed and slides
     itself away on scroll down, so one static offset cannot be right in both
     states: while the header is hidden it leaves a band of the page showing
     through above the bar.

     The theme flags the hidden state with `sf__header-hidden` on the sticky
     host, so the offset is driven by that class rather than by the painted
     geometry: `offsetHeight` ignores the transform, which means the target is
     known the moment the class flips and the bar can then transition in step
     with the header instead of chasing a value that is still animating. */

  var HEADER_SELECTOR = '.sf__header-sticky .header__wrapper';
  var HEADER_FALLBACK = '.header__wrapper';
  var HEADER_HOST_SELECTOR = '.sf__header-sticky';
  var HEADER_HIDDEN_CLASS = 'sf__header-hidden';
  var MAX_HEADER_HEIGHT = 200;

  function headerOffset(header) {
    if (!header) return 0;

    var height = Math.round(header.offsetHeight || 0);
    if (!height || height > MAX_HEADER_HEIGHT) return 0;

    var host = header.closest ? header.closest(HEADER_HOST_SELECTOR) : null;
    var hidden = !!host && host.classList.contains(HEADER_HIDDEN_CLASS);
    if (hidden) return 0;

    /* No host class to read: fall back to the painted box so a theme that hides
       the header some other way still ends up flush against the top. */
    if (!host) {
      var bottom = Math.round(header.getBoundingClientRect().bottom);
      if (bottom <= 0) return 0;
    }

    return height;
  }

  function initStickyOffset(root) {
    var filter = root.querySelector('.splux-blog-gallery__filter');
    if (!filter || !window.requestAnimationFrame) return;

    var header = null;
    var applied = null;
    var queued = false;

    function measure() {
      queued = false;

      if (!header || !document.contains(header)) {
        header = document.querySelector(HEADER_SELECTOR) ||
          document.querySelector(HEADER_FALLBACK);
      }

      var next = headerOffset(header);
      if (next === applied) return;
      applied = next;
      root.style.setProperty('--splux-blog-gallery-header-offset', next + 'px');
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    /* Images and webfonts settle after load and change the header height. */
    window.addEventListener('load', schedule);
    measure();
  }

  /* ---------------------------------------------------------------------------
     Section controller
     --------------------------------------------------------------------------- */

  function initSection(root) {
    var dropdowns = [];
    var clearBtn = root.querySelector('[data-splux-bg-clear]');
    var baseUrl = root.getAttribute('data-splux-bg-base') || '';

    var context = {
      closeAll: function (except) {
        dropdowns.forEach(function (dropdown) {
          if (dropdown && dropdown.element !== except) dropdown.close();
        });
      },

      /* Called after a selection changed. A /tagged/ archive holds a single tag,
         so the dropdown that just changed wins and the rest are cleared: the bar
         must describe the archive the page is about to show, not a combination
         Shopify cannot express there. */
      apply: function (origin) {
        if (origin) {
          dropdowns.forEach(function (dropdown) {
            if (dropdown && dropdown !== origin && dropdown.value() !== '') {
              dropdown.select('', true);
            }
          });
        }

        var chosen = origin && origin.value() !== '' ? origin : null;
        if (!chosen) {
          dropdowns.forEach(function (dropdown) {
            if (!chosen && dropdown && dropdown.value() !== '') chosen = dropdown;
          });
        }

        var tag = chosen ? chosen.tagged() : '';
        if (!tag) {
          navigate(baseUrl);
          return;
        }

        navigate(baseUrl.replace(/\/+$/, '') + '/tagged/' + tag);
      }
    };

    Array.prototype.slice
      .call(root.querySelectorAll('[data-splux-bg-dropdown]'))
      .forEach(function (element) {
        var dropdown = initDropdown(element, context);
        if (dropdown) dropdowns.push(dropdown);
      });

    /* A /tagged/ archive arrives with one dropdown already selected. "Clear all"
       exists for exactly that case: it returns to the unfiltered gallery. */
    var hasPreset = dropdowns.some(function (dropdown) {
      return dropdown.value() !== '';
    });

    if (clearBtn) {
      clearBtn.classList.toggle('is-visible', hasPreset);
      clearBtn.addEventListener('click', function (event) {
        event.stopPropagation();
        dropdowns.forEach(function (dropdown) {
          dropdown.select('', true);
        });
        navigate(baseUrl);
      });
    }

    document.addEventListener('click', function () {
      context.closeAll();
    });
  }

/* ---------------------------------------------------------------------------
     Browse by model
     --------------------------------------------------------------------------- */

  /* The list ships collapsed to `models_visible` links. Expanding is a class
     flip, not a re-render: the hidden links are already in the DOM, so the
     toggle costs nothing and the block stays crawlable while closed. */
  function initModels(root) {
    var list = root.querySelector('[data-splux-bg-models-list]');
    var toggle = root.querySelector('[data-splux-bg-models-toggle]');
    if (!list || !toggle) return;

    function expanded() {
      return list.getAttribute('data-collapsed') !== 'true';
    }

    function render() {
      var open = expanded();
      toggle.textContent = open
        ? toggle.getAttribute('data-less') || 'Show less'
        : toggle.getAttribute('data-more') || 'Show more';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    toggle.addEventListener('click', function () {
      list.setAttribute('data-collapsed', expanded() ? 'true' : 'false');
      render();
    });

    render();
  }

  function init(root) {
    initSection(root);
    initModels(root);
    initStickyOffset(root);
  }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR)).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
